import React from 'react';
import styled from '@emotion/styled';

import HandyPageWrapper from '~Scenes/Handy/Components/HandyPageWrapper';
import { useI18n, useLicenses, useLogger } from '~Hooks';
import SettingsToggle, { Setting } from '~Scenes/Handy/Components/SettingsToggle';
import { StorageData, useStorage } from '~Components/Storage';
import RadioButtonSelect from '~ComponentsImproved/RadioSelector';

const RadioButtonsBox = styled.div`
  border-radius: 8px;
  background: var(--colorBlueGray1100);
  margin-top: 16px;
`;

const HandyReceipts = () => {
  const { i18n } = useI18n();
  const licenses = useLicenses();
  const [storage, updateStorage] = useStorage();
  const logger = useLogger('handy-receipt-settings');

  // Update storage with logging
  function commitToStorage(data: Partial<StorageData>) {
    logger.info('Updating Receipt Settings', { fields: Object.keys(data) });
    updateStorage(data);
  }

  // Build the toggle settings list
  const SETTING_LIST: Setting[] = [
    {
      key: 'truncateReceiptItemNames',
      label: i18n('Settings.ShortenChequeItemNames'),
      description: i18n('Settings.ShortenChequeItemNames.Text'),
    },
    {
      key: 'hideZeroAmountItemsInCustomerPrints',
      label: i18n('Settings.HideZeroAmountItemsInCustomerPrints.Title'),
    },
    {
      key: 'receiptShowOnlyOfferName',
      label: i18n('Settings.ReceiptShowOnlyOfferName.Title'),
      description: i18n('Settings.ReceiptShowOnlyOfferName.Description'),
    },
  ];

  // Add tip suggestions if TSR license is available
  if (licenses.hasTSR) {
    SETTING_LIST.push({
      key: 'showTipSuggestions',
      label: i18n('Settings.ShowTipSuggestions.Title'),
      description: i18n('Settings.ShowTipSuggestions.Description'),
    });
  }

  // Radio button configuration for receipt print settings
  const radioButtonsConfig: {
    containerLabel: string;
    containerDesc?: string;
    name: string;
    buttons: Array<{
      id: string;
      label: string;
      value: string;
    }>;
  }[] = [
    {
      containerLabel: i18n('Settings.ReceiptSettings'),
      containerDesc: i18n('Settings.ReceiptSettings.Desc'),
      name: 'receiptSettings',
      buttons: [
        {
          id: 'ALWAYS',
          label: i18n('Settings.ReceiptSettings.Always'),
          value: 'ALWAYS',
        },
        {
          id: 'ASK',
          label: i18n('Settings.ReceiptSettings.Ask'),
          value: 'ASK',
        },
        {
          id: 'MANUAL',
          label: i18n('Settings.ReceiptSettings.Manual'),
          value: 'MANUAL',
        },
      ],
    },
  ];

  // Handle receipt print setting changes
  const handlePrintReceiptSetting = (value: string) => {
    logger.info('Changing receipt print setting', { value });

    if (value === 'ALWAYS') {
      commitToStorage({ askToPrint: false, manualReceiptPrint: false });
    }

    if (value === 'ASK') {
      commitToStorage({ askToPrint: true, manualReceiptPrint: false });
    }

    if (value === 'MANUAL') {
      commitToStorage({ askToPrint: false, manualReceiptPrint: true });
    }
  };

  // Determine which radio button should be selected
  const printSettingsSelected = storage.manualReceiptPrint
    ? 'MANUAL'
    : storage.askToPrint
    ? 'ASK'
    : 'ALWAYS';

  return (
    <HandyPageWrapper pageTitle={i18n('Handy.Settings.ReceiptsTitle')}>
      {/* Radio buttons for receipt print mode */}
      <RadioButtonsBox>
        {radioButtonsConfig.map((config) => (
          <RadioButtonSelect
            key={config.name}
            data={config}
            selected={printSettingsSelected}
            handleChange={(value) => handlePrintReceiptSetting(value)}
          />
        ))}
      </RadioButtonsBox>

      {/* Toggle settings */}
      <SettingsToggle settingList={SETTING_LIST} />
    </HandyPageWrapper>
  );
};

export default HandyReceipts;
